/**
 * I verdetti presi ma non ancora consegnati.
 *
 * **La mano dell'operatore non aspetta la rete.** Un verdetto e' una decisione
 * gia' presa: farla attendere il viaggio di andata e ritorno significa mettere
 * la latenza dentro il tempo di lavoro, e su centinaia di candidati al giorno
 * quella somma e' il costo della verifica.
 *
 * **Ma «prendere in carico» e' una promessa.** La schermata avanza su questa
 * promessa: se la decisione non finisce da nessuna parte — limite raggiunto,
 * deposito che non scrive, nessuna sessione per conto di cui decidere — e
 * nessuno lo dice, il verdetto e' perso e l'operatore lo sa solo alla consegna,
 * quando l'anomalia non c'e' piu'. Qui non esiste un fallimento silenzioso: o
 * la decisione e' al sicuro, o chi ha deciso lo viene a sapere subito e resta
 * sul candidato.
 *
 * **Una strada sola: gli intenti di sessione (GI-172).** La vecchia strada
 * diretta e' finita. Non era solo piu' vecchia: scriveva senza gettone e senza
 * versione attesa, quindi poteva sovrascrivere il verdetto di una sessione che
 * teneva quel candidato in mano — proprio il doppione che i lease esistono per
 * impedire. Il servizio ora la rifiuta con `review_protocol_required` dove il
 * protocollo e' richiesto; questa casella d'uscita non la percorre piu'
 * affatto. Un banco senza sessione **guarda** il lavoro, non lo cambia.
 *
 * **`localStorage` non e' piu' l'autorita'.** La coda vive nel deposito
 * versionato: su `localStorage` ogni scrittura e' «leggi tutto, riscrivi
 * tutto», e due schede che riscrivono tutto insieme perdono il lavoro di una
 * delle due senza dirlo. Cio' che era rimasto nel vecchio deposito non si
 * butta: si adotta come lavoro non consegnato e **si dice**.
 */
import { detectionIn, isCoverage } from './reviewProtocol'
import type { ReviewIntent } from './reviewProtocol'
import { memoryStore, openStore } from './reviewStore'
import type { ReviewStore } from './reviewStore'
import { NotTaken, reviewSync } from './reviewSync'
import type { Protocol, ReviewSync, Undone } from './reviewSync'
import type { Box, Point } from './types'

/** Cio' che una decisione porta con se' quando nasce dentro una sessione: il
 *  gettone del lease e lo stato del candidato da cui parte. */
export interface Reserved {
  leaseToken?: string | null
  candidateVersion?: number
}

/**
 * Una dichiarazione di copertura (GI-169), nei tre modi in cui si fa.
 *
 * Passa da **questa** casella d'uscita e non da una seconda: la copertura si
 * dichiara fuori linea come si decide fuori linea, e due code vorrebbero dire
 * due limiti, due riconciliazioni e due modi di raccontare un rifiuto — con la
 * certezza che il difetto corretto in una resti nell'altra.
 *
 * Nessuna porta un gettone di lease: non c'e' un candidato preso in mano. Cio'
 * che le rende sicure e' il soggetto — fotogramma o elemento — su cui il
 * servizio tiene una sola dichiarazione in volo per volta.
 */
export type Declaration =
  | {
      kind: 'manual_finding'
      photoId: string
      elementId: string
      code: string
      level: number
      box: Box
      note?: string
      /** Ignorata: un'anomalia nuova non sostituisce niente, la versione e' 0. */
      expectedCandidateVersion?: number
    }
  | {
      kind: 'photo_reviewed'
      photoId: string
      reviewed: boolean
      expectedCandidateVersion?: number
    }
  // Motivo nullo e' il ritiro della dichiarazione: l'elemento torna a essere un
  // buco da colmare, che e' una cosa diversa dal non averlo mai dichiarato.
  | {
      kind: 'not_inspected'
      elementId: string
      reasonCode: string | null
      note?: string
      expectedCandidateVersion?: number
    }

export type Intent =
  // Il contorno (GI-228) viaggia con la conferma come il riquadro: il servizio
  // ne ricava il riquadro che lo contiene, e il resto del prodotto non cambia.
  | ({ kind: 'confirm'; detectionId: string; level: number; box?: Box; polygon?: Point[] } & Reserved)
  | ({ kind: 'reject'; detectionId: string; reasonCode: string } & Reserved)
  // Un annullamento senza candidato e' quello della pagina appena riaperta:
  // sa che si vuole tornare indietro, non piu' su che cosa.
  | ({ kind: 'undo'; detectionId: string | null } & Reserved)
  | Declaration

/** Se una decisione parla di copertura invece che di un candidato. */
export function isDeclaration(intent: Intent): intent is Declaration {
  return isCoverage(intent.kind)
}

/** La sessione per conto di cui si decide. */
export interface Session {
  sessionId: string
  reviewEpoch: number
}

/**
 * Cio' che non e' passato, con quanto serve per dirlo e per rimediare.
 *
 * `code` e `message` insieme: la schermata decide sul codice, l'operatore legge
 * la frase. Tenerne uno solo obbligherebbe una delle due a interpretare
 * l'altra.
 *
 * `keep` distingue i due momenti. Il fallimento **immediato** — il limite, il
 * deposito che non scrive, la sessione che manca — arriva mentre l'operatore e'
 * ancora sul candidato: li' deve restare, perche' quella decisione va ripresa
 * adesso. Il rifiuto che arriva **dopo**, dalla consegna, lo trova venti
 * candidati piu' avanti: riportarlo indietro di forza gli farebbe perdere il
 * segno. Quello resta scritto, e si riprende quando vuole.
 */
export interface Rejected {
  intent: Intent
  message: string
  code: string | null
  keep: boolean
}

/**
 * Che fine ha fatto la decisione presa su un candidato.
 *
 * Non sono gli stati del deposito: sono quelli che cambiano qualcosa per chi ha
 * deciso. `local` e `decision_pending` diventano la stessa cosa — «in
 * consegna» — perche' la differenza fra scritta di qua e registrata di la'
 * riguarda noi, non lui. `withdrawn` invece si distingue da `applied`: un
 * verdetto ritirato e un verdetto registrato sono opposti, e confonderli
 * significherebbe rimettere mano a un candidato che non ne ha bisogno o
 * lasciarne uno senza verdetto.
 */
export type IntentState =
  | 'decision_pending'
  | 'applied'
  | 'withdrawn'
  | 'rejected'
  | 'stale'

export interface Outbox {
  /** Prende in carico il verdetto e restituisce subito. */
  add(intent: Intent): void
  /** Quanti aspettano di partire. */
  size(): number
  /** Consegna finche' puo'. Sicuro da chiamare piu' volte. */
  flush(): Promise<void>
  /**
   * Che fine hanno fatto le decisioni, candidato per candidato.
   *
   * Serve al nastro dei lease: senza, un candidato deciso e uno mai guardato
   * si somigliano, e l'operatore non ha modo di sapere quale verdetto sia
   * ancora per strada. Si legge quando `onChange` dice che qualcosa e'
   * cambiato: tenerne una copia qui vorrebbe dire un secondo elenco da
   * mantenere allineato al deposito.
   */
  states(): Promise<Map<string, IntentState>>
  /**
   * Un lease nuovo su questi candidati: i rifiuti vecchi non sono piu' la loro
   * ultima parola. Restituisce i candidati davvero liberati, per chi deve
   * togliere anche l'avviso che li riguardava.
   */
  release(detectionIds: string[], exceptSession?: string): Promise<string[]>
  /**
   * Che fine hanno fatto le dichiarazioni di copertura, soggetto per soggetto.
   *
   * Separata da `states` e non la stessa mappa: li' la chiave e' il candidato,
   * qui e' il fotogramma o l'elemento. Una mappa sola costringerebbe ogni
   * lettore a sapere quale genere di chiave ha in mano, e il banco — che di
   * copertura non parla — a filtrare righe che non gli servono.
   *
   * Le chiavi sono quelle di `subjectOf`, cosi' la schermata chiede «che fine ha
   * fatto la dichiarazione su *questo*» senza ricostruire un nome.
   */
  declarations(): Promise<Map<string, IntentState>>
  /**
   * Chiede al servizio che fine hanno fatto le proprie decisioni.
   *
   * Non applica niente: e' il gesto che deve precedere una nuova acquisizione
   * (GI-172), perche' prendere altri candidati prima di sapere significa
   * decidere venti volte su uno stato smentito.
   */
  reconcile(sessionId: string): Promise<void>
}

export interface OutboxOptions {
  /** Che cosa fare quando una decisione non e' passata. */
  onRejected?: (rejected: Rejected) => void
  onChange?: (size: number) => void
  /** Iniettabile perche' le prove non debbano aspettare davvero. */
  sleep?: (ms: number) => Promise<void>
  /** Il deposito che tiene la coda. `null` per lavorare solo in memoria. */
  store?: Promise<ReviewStore> | null
  /** Da dove adottare cio' che era rimasto nel vecchio deposito. */
  storage?: Storage | null
  /** I tre gesti verso il servizio. Iniettabili per le prove. */
  protocol?: Protocol
  /**
   * Per conto di quale sessione si decide.
   *
   * Una funzione e non un valore: la casella d'uscita e' una sola per tutto il
   * turno, mentre la sessione arriva dopo — con la prima lettura della coda — e
   * puo' essere riaperta. Un valore fissato alla costruzione direbbe «nessuna
   * sessione» per sempre.
   */
  session?: (() => Session | null) | null
}

/** Come si nomina, a chi ha deciso, l'ultima riga scritta su un candidato.
 *
 * Nullo dove non c'e' niente da dire: una riga adottata dal vecchio deposito
 * non appartiene a nessuna sessione, e dichiararne lo stato accanto a un
 * candidato del batch di oggi vorrebbe dire attribuirgli un verdetto che
 * nessuno ha dato qui. */
function spoken(intent: ReviewIntent): IntentState | null {
  if (intent.state === 'local' || intent.state === 'decision_pending') return 'decision_pending'
  if (intent.state === 'rejected') return 'rejected'
  if (intent.state === 'stale') return 'stale'
  if (intent.state !== 'applied') return null
  return intent.kind === 'reversal' ? 'withdrawn' : 'applied'
}

/** Il contenuto di una decisione, nella forma che il servizio riconosce. */
export function payloadOf(intent: Intent): Record<string, unknown> {
  if (intent.kind === 'manual_finding') {
    // La nota si mette solo se c'e': una nota vuota e una nota assente sono la
    // stessa cosa per il servizio, ma cambierebbero l'impronta della richiesta.
    const found: Record<string, unknown> = {
      code: intent.code,
      level: intent.level,
      box: intent.box,
    }
    if (intent.note) found.note = intent.note
    return found
  }
  if (intent.kind === 'photo_reviewed') return { reviewed: intent.reviewed }
  if (intent.kind === 'not_inspected') {
    // `declared` e' il campo che il registro legge: inferirlo dal motivo nullo
    // qui e non mandarlo farebbe rifiutare ogni buco con `declared_required`.
    if (intent.reasonCode === null) {
      const withdrawn: Record<string, unknown> = { declared: false }
      if (intent.note) withdrawn.note = intent.note
      return withdrawn
    }
    const declared: Record<string, unknown> = {
      declared: true,
      reason_code: intent.reasonCode,
    }
    if (intent.note) declared.note = intent.note
    return declared
  }
  if (intent.kind === 'reject') {
    return { decision: 'rejected', reason_code: intent.reasonCode }
  }
  if (intent.kind === 'undo') return {}
  // Il riquadro e il contorno si mettono solo se ci sono: un `box: null`
  // esplicito e un riquadro assente sono la stessa cosa per il servizio, ma
  // cambierebbero l'impronta.
  const confirmed: Record<string, unknown> = { decision: 'confirmed', final_level: intent.level }
  if (intent.box) confirmed.box = intent.box
  if (intent.polygon) confirmed.polygon = intent.polygon
  return confirmed
}

const NOWHERE: Box = { x: 0, y: 0, width: 0, height: 0 }

/** Dal deposito alla decisione, per poterla nominare a chi l'ha presa. */
export function asLegacyIntent(row: ReviewIntent): Intent {
  if (isCoverage(row.kind)) return asDeclaration(row)
  if (row.kind === 'reversal') return { kind: 'undo', detectionId: row.detectionId }
  if (row.payload.decision === 'rejected') {
    return {
      kind: 'reject',
      detectionId: row.detectionId ?? '',
      reasonCode: String(row.payload.reason_code ?? ''),
    }
  }
  const box = row.payload.box as Box | undefined
  const polygon = row.payload.polygon as Point[] | undefined
  const confirmed: Intent = {
    kind: 'confirm',
    detectionId: row.detectionId ?? '',
    level: Number(row.payload.final_level ?? 0),
  }
  return {
    ...confirmed,
    ...(box ? { box } : {}),
    ...(polygon ? { polygon } : {}),
  }
}

/** Dal deposito alla dichiarazione, per poter dire *di che cosa* si parlava.
 *
 * Il soggetto si rilegge dalla riga e non si ricostruisce a mano: un rifiuto
 * che arriva alla consegna deve poter nominare il fotogramma o l'elemento anche
 * dopo un ricaricamento, quando in memoria non c'e' piu' nulla. */
function asDeclaration(row: ReviewIntent): Declaration {
  if (row.kind === 'photo_reviewed') {
    return {
      kind: 'photo_reviewed',
      photoId: row.photoId ?? '',
      reviewed: row.payload.reviewed !== false,
    }
  }
  if (row.kind === 'not_inspected') {
    const reason = row.payload.reason_code
    const declared = row.payload.declared
    return {
      kind: 'not_inspected',
      elementId: row.elementId ?? '',
      reasonCode:
        declared === false || reason === null || reason === undefined ? null : String(reason),
    }
  }
  return {
    kind: 'manual_finding',
    photoId: row.photoId ?? '',
    elementId: row.elementId ?? '',
    code: String(row.payload.code ?? ''),
    level: Number(row.payload.level ?? 0),
    box: (row.payload.box as Box | undefined) ?? NOWHERE,
  }
}

export function outbox(options: OutboxOptions): Outbox {
  const storage = options.storage === undefined ? window.localStorage : options.storage
  // Quante decisioni aspettano ancora, secondo il deposito. Non si conta a
  // mano: il conteggio vero e' quello di chi le tiene.
  let waiting = 0
  /*
   * Le prese in carico si mettono in fila, la consegna no.
   *
   * In fila perche' l'ordine in cui l'operatore ha deciso e' quello in cui si
   * scrive, e due `add` sovrapposte lo deciderebbero a caso. Ma **senza**
   * aspettare la consegna: con la rete giu' un tentativo puo' durare un
   * minuto, e in quel minuto le decisioni successive resterebbero fuori dal
   * deposito — cioe' perse, se la pagina si chiude. Mettere al sicuro viene
   * prima di consegnare.
   */
  let queued: Promise<void> = Promise.resolve()

  const ready = boot()

  async function boot(): Promise<ReviewSync> {
    const sync = reviewSync({
      store: await opened(),
      protocol: options.protocol,
      sleep: options.sleep,
      storage,
      onChange: (many) => {
        waiting = many
        options.onChange?.(many)
      },
      onSettled: (settled) => {
        options.onRejected?.({
          intent: asLegacyIntent(settled.intent),
          message: settled.message,
          code: settled.code,
          // Arriva dalla consegna, quando l'operatore e' gia' avanti: si dice,
          // non si trascina indietro.
          keep: false,
        })
      },
    })
    // Cio' che era rimasto — dal vecchio deposito, o da una pagina chiusa con
    // la rete giu' — si adotta, si dice, e si chiede al servizio che fine
    // abbia fatto prima di riscrivere qualsiasi cosa.
    await sync.ready()
    return sync
  }

  async function opened(): Promise<ReviewStore> {
    if (options.store === null) return memoryStore()
    try {
      return await (options.store ?? openStore())
    } catch {
      // Nessun deposito — finestra privata, quota negata. Si continua in
      // memoria: fermare il lavoro sarebbe la reazione peggiore, e tornare
      // alla strada senza sessione sarebbe il doppione.
      return memoryStore()
    }
  }

  function codeOf(error: unknown): string {
    return error instanceof NotTaken ? error.code : 'not_taken'
  }

  function messageOf(error: unknown): string {
    return error instanceof Error
      ? error.message
      : "La decisione non e' stata presa in carico."
  }

  function refuse(intent: Intent, code: string, message: string, keep: boolean): void {
    options.onRejected?.({ intent, message, code, keep })
  }

  /** Che cosa e' successo a un annullamento, detto a chi lo ha chiesto. */
  function told(intent: Intent, undone: Undone): void {
    if (undone.kind === 'removed' || undone.kind === 'reversal') return
    if (undone.kind === 'too_late') {
      refuse(
        intent,
        'undo_deadline_elapsed',
        'I cinque secondi per annullare sono passati: riprendi il candidato e decidi di nuovo.',
        false,
      )
      return
    }
    if (undone.kind === 'nothing_written') {
      refuse(
        intent,
        'nothing_written',
        `Non c'e' niente da annullare: quel verdetto non era stato registrato.` +
          ` ${undone.intent.note ?? ''}`.trimEnd(),
        false,
      )
      return
    }
    if (undone.kind === 'unreachable') {
      refuse(
        intent,
        'undo_unresolved',
        "Non si e' potuto sapere se quel verdetto e' stato scritto:" +
          ' riprova quando la rete torna.',
        false,
      )
      return
    }
    refuse(intent, 'nothing_to_undo', "Su questo candidato non c'e' una decisione da annullare.", false)
  }

  async function take(intent: Intent, session: Session | null): Promise<void> {
    if (session === null) {
      // Senza sessione non si scrive. Il servizio rifiuterebbe comunque: dirlo
      // qui evita all'operatore di scoprirlo dopo aver deciso venti candidati.
      refuse(
        intent,
        'review_protocol_required',
        "Questo banco non ha una sessione di verifica: si puo' guardare il lavoro," +
          ' non deciderlo. Riapri la coda dal pool.',
        true,
      )
      return
    }
    const sync = await ready
    if (intent.kind === 'undo') {
      try {
        told(intent, await sync.undoLatest(session.sessionId, intent.detectionId))
      } catch (error: unknown) {
        // Anche il ritiro e' una decisione da scrivere: puo' non entrare nella
        // coda, o il deposito puo' non dire quale verdetto ritirare. Tacere
        // qui lascia l'operatore convinto di aver annullato.
        refuse(intent, codeOf(error), messageOf(error), true)
      }
      return
    }
    try {
      await sync.add(
        isDeclaration(intent)
          ? {
              sessionId: session.sessionId,
              detectionId: null,
              photoId: 'photoId' in intent ? intent.photoId : null,
              elementId: 'elementId' in intent ? intent.elementId : null,
              // Nessun gettone: non c'e' un candidato preso in mano. La versione
              // attesa e' la sequenza del soggetto — zero solo per un'anomalia
              // nuova, che non sostituisce niente.
              leaseToken: null,
              reviewEpoch: session.reviewEpoch,
              expectedCandidateVersion:
                intent.kind === 'manual_finding' ? 0 : (intent.expectedCandidateVersion ?? 0),
              kind: intent.kind,
              payload: payloadOf(intent),
            }
          : {
              sessionId: session.sessionId,
              detectionId: intent.detectionId,
              leaseToken: intent.leaseToken ?? null,
              reviewEpoch: session.reviewEpoch,
              expectedCandidateVersion: intent.candidateVersion ?? 0,
              kind: 'verdict',
              payload: payloadOf(intent),
            },
      )
    } catch (error: unknown) {
      // La decisione non e' finita da nessuna parte. La schermata e' gia'
      // avanzata su una promessa che non si e' potuta mantenere: si dice, e si
      // riporta l'operatore sul candidato che deve ancora decidere.
      refuse(intent, codeOf(error), messageOf(error), true)
    }
  }

  function flush(): Promise<void> {
    // Si aspetta anche cio' che e' in presa in carico: senza, «ho consegnato»
    // sarebbe vero mentre una decisione non e' ancora nemmeno scritta.
    return queued.then(async () => {
      await (await ready).flush()
    })
  }

  return {
    add(intent: Intent): void {
      const session = options.session?.() ?? null
      queued = queued.then(() => take(intent, session)).catch(() => undefined)
      // La consegna parte da sola, ma non trattiene la presa in carico della
      // decisione seguente.
      void flush()
    },

    size(): number {
      return waiting
    },

    flush,

    async release(detectionIds: string[], exceptSession?: string): Promise<string[]> {
      const sync = await ready
      return sync.release(detectionIds, exceptSession)
    },

    async states(): Promise<Map<string, IntentState>> {
      const sync = await ready
      const states = new Map<string, IntentState>()
      for (const [subject, intent] of await sync.latest()) {
        const detectionId = detectionIn(subject)
        if (detectionId === null) continue
        const said = spoken(intent)
        if (said !== null) states.set(detectionId, said)
      }
      return states
    },

    async declarations(): Promise<Map<string, IntentState>> {
      const sync = await ready
      const states = new Map<string, IntentState>()
      for (const [subject, intent] of await sync.latest()) {
        if (detectionIn(subject) !== null) continue
        const said = spoken(intent)
        if (said !== null) states.set(subject, said)
      }
      return states
    },

    async reconcile(sessionId: string): Promise<void> {
      await (await ready).reconcile(sessionId)
    },
  }
}
